/**
 * How big the HUD is drawn.
 *
 * Writes to [hudScale](../../hud/hudScale.ts), which the frame reads per tick and puts on the layer
 * as one custom property. Every length in every element sheet is `em` against that, so a drag
 * relays out the whole HUD rather than stretching a picture of it, and it does so in both skins at
 * once: neither has a size of its own to keep in step.
 *
 * The bounds and the default are that module's, not this file's. A knob names a quantity and hands
 * a value to whoever owns it; owning the range as well would put the same three numbers in two
 * places and let the slider offer a size the HUD clamps away.
 */

import { DEFAULT_HUD_SCALE, MAX_HUD_SCALE, MIN_HUD_SCALE, setHudScale } from '@/ui/hud/hudScale';
import { rangeApply } from '@/ui/settings/controls/rangeSlider';
import { SETTING_HUD_SCALE_HINT, SETTING_HUD_SCALE_LABEL } from '@/text/settingsText';
import type { SettingDefinition } from '@/ui/settings/settingContract';

/** Five percent a step: fine enough to find a size, coarse enough to land on one again. */
const STEP = 0.05;

export const HUD_SCALE: SettingDefinition = {
  id: 'hudScale',
  section: 'gameplay',
  label: SETTING_HUD_SCALE_LABEL,
  hint: SETTING_HUD_SCALE_HINT,
  control: {
    kind: 'range',
    min: MIN_HUD_SCALE,
    max: MAX_HUD_SCALE,
    step: STEP,
    // A percentage, because a HUD is bigger or smaller than the one the game shipped with, and
    // 1.15 is a number nobody can picture.
    format: (value: number): string => `${String(Math.round(value * 100))}%`,
  },
  defaultValue: DEFAULT_HUD_SCALE,
  apply: rangeApply(setHudScale),
};
